import { type ReactNode, useState } from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/shared/lib/utils';

interface CollapseProps {
  title: ReactNode;
  description?: ReactNode;
  rightContent?: ReactNode;
  children?: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  indicatorPosition?: 'left' | 'right';
  size?: 'lg' | 'md';
  openChevronRotationDeg?: number;
}

const Collapse = ({
  title,
  description,
  rightContent,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  className,
  headerClassName,
  contentClassName,
  indicatorPosition = 'right',
  size = 'lg',
  openChevronRotationDeg = 90,
}: CollapseProps) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = typeof open === 'boolean';
  const isOpen = isControlled ? Boolean(open) : internalOpen;

  const setOpen = (nextOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const isLarge = size === 'lg';
  const chevron = (
    <ChevronRight
      className={cn(
        isLarge ? 'h-4 w-4' : 'h-3.5 w-3.5',
        'text-muted-foreground transition-transform duration-150 ease-out',
      )}
      style={{ transform: `rotate(${isOpen ? openChevronRotationDeg : 0}deg)` }}
    />
  );

  const titleClassName = isLarge
    ? 'text-[17px] font-semibold tracking-[-0.01em] text-foreground'
    : 'text-[14px] font-semibold text-foreground';
  const descriptionClassName = 'text-xs text-muted-foreground';
  const rightContentClassName = 'text-xs text-muted-foreground tabular-nums';

  return (
    <div className={cn('w-full', className)}>
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between text-left',
          isLarge ? 'min-h-[52px]' : 'min-h-[48px]',
          headerClassName,
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {indicatorPosition === 'left' ? chevron : null}
          <div className={cn('flex min-w-0 items-baseline', isLarge ? 'gap-3' : 'gap-2')}>
            <span className={cn(titleClassName, 'truncate')}>
              {title}
            </span>
            {description ? (
              <span className={cn(descriptionClassName, 'truncate')}>
                {description}
              </span>
            ) : null}
          </div>
        </div>
        <div className={cn('flex items-center', isLarge ? 'gap-3' : 'gap-2')}>
          {rightContent ? (
            <span className={rightContentClassName}>
              {rightContent}
            </span>
          ) : null}
          {indicatorPosition === 'right' ? chevron : null}
        </div>
      </button>

      <div
        className={cn(
          'grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-in-out',
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              'pb-2 transition-opacity duration-200 ease-in-out',
              isOpen ? 'opacity-100' : 'opacity-0',
              contentClassName,
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Collapse;
